import { ReactNode, useEffect } from 'react'

import {
  CashtagsThemeVariantProvider,
  Colors,
  getColorsByThemeVariant,
  Provider as DesignSystemProvider,
  ThemeVariant,
} from '@cash-design-system/react'
import { useFeatureFlag } from '@cash-web/shared-data-access-feature-flags'
import { useLocalStorage } from '@cash-web/shared-ui-use-storage'
import { replaceWithCDNImageUrl } from '@cash-web/shared-util-url'

import { useDarkMode } from '../hooks/useDarkMode'

const BACKGROUND_FALLBACK_KEY = 'cash-web-bg-fallback-color'
const HTTPS_IMAGE_URL = 'https'

type ThemeProviderProps = {
  bodyBackground?: Colors
  optOutOfDarkMode?: boolean
  themeVariant?: ThemeVariant
}

/**
 * A stripped down theme provider for the cashtags page to expose tokens only, until cashtags are fully theme-ified
 */
export const CashtagsThemeProvider = ({ children }: { children: ReactNode }) => {
  const isDarkMode = useDarkMode()
  return <CashtagsThemeVariantProvider darkMode={isDarkMode}>{children}</CashtagsThemeVariantProvider>
}

export const ThemeProvider = ({
  bodyBackground = undefined,
  children,
  optOutOfDarkMode = false,
  themeVariant = 'default',
}: React.PropsWithChildren<ThemeProviderProps>) => {
  const isDarkMode = useDarkMode()

  // For the arcade component migration, we need to know which components are enabled.
  // We can't (and shouldn't) use the `useFeatureFlag` hook in CDS-React components directly due to Nx boundaries
  // We pass the feature flags to the `DesignSystemProvider` and the `DesignSystemProvider` will pass it down to the `ArcadeMigrationProvider`
  const arcadeComponentMigrationFeatureFlags = useFeatureFlag('cash-web-arcade-migration')

  // For the cash sans migration
  const shouldUseCashSans = useFeatureFlag('cash-web-arcade-migration-cash-sans') === 'ENABLED'

  // if `optOutOfDarkMode` is true, we don't want to apply dark mode regardless of the user's preference
  // fallback to `isDarkMode` if not opted out
  const shouldUseDarkMode = optOutOfDarkMode ? false : isDarkMode

  const { background } = getColorsByThemeVariant({ themeVariant, darkMode: shouldUseDarkMode })

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setBackgroundColor] = useLocalStorage(BACKGROUND_FALLBACK_KEY, background)

  useEffect(() => {
    setBackgroundColor(background)
  }, [background, setBackgroundColor])

  return (
    <DesignSystemProvider
      bodyBackground={bodyBackground}
      darkMode={shouldUseDarkMode}
      themeVariant={themeVariant}
      featureFlags={arcadeComponentMigrationFeatureFlags}
      shouldUseCashSans={shouldUseCashSans}
      imageUrlHandler={({ url, width }: { url: string; width: number }) => {
        return url.startsWith(HTTPS_IMAGE_URL) ? replaceWithCDNImageUrl({ url, width }) : url
      }}
    >
      {children}
    </DesignSystemProvider>
  )
}
